import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { useMemo } from 'react';

import '@/features/metric-studio/studio.css';

import { useNavPrefetcher } from '@/components/layout/use-nav-prefetcher';
import {
  isActiveDestination,
  navDestination,
  type NavDestination,
} from '@/features/metric-studio';
import type { HoverPrefetcher } from '@/features/metric-studio/prefetch';
import { useAsOf } from '@/lib/asof/use-asof';
import {
  isModelOutputNavVisible,
  useModelOutputAccess,
  visibleNavGroups,
} from '@/lib/compliance/model-output-access';
import { registryQuery, seriesQuery } from '@/lib/api/queries';
import { rangeToWindow } from '@/lib/dashboard/range';
import { useMarket } from '@/lib/market/market-context';
import { DEFAULT_RANGE } from '@/features/metric-studio';
import { useSymbol } from '@/stores/symbolStore';

/**
 * =============================================================================
 *  §10.1 的 Sidebar。
 *
 *  **P3 起，明細頁那一層完全由 `GET /meta/metrics` 的 `nav` 產生。**
 *  ROADMAP §6 交付項逐字：「Sidebar 完全由 `GET /meta/metrics` 的 `nav` 產生，
 *  **前端不得有任何硬寫的 metric 清單**（以 grep 驗證）」，驗收 #2：
 *
 *      grep -rn "<驗收 #2 的那兩個 metric key>" frontend/src --include=*.tsx
 *
 *  （那兩個 key 在這裡刻意寫成佔位符：**grep 不區分程式碼與註解**，
 *   把它們寫進註解等於讓驗收自己失敗。完整指令在 ROADMAP §6 驗收 #2。）
 *
 *  所以這個檔案裡沒有任何 metric 的名字，也沒有任何一段「籌碼有這幾頁」的知識。
 *  它只做三件事：把 `nav` 的分組畫成標題、把 `href_template` 翻成 `<Link to params>`
 *  （`features/metric-studio/nav.ts`），以及在 hover 時預取那一頁的資料。
 *
 *  上面那幾個固定目的地（儀表板 / 自選股 / 庫存股 / 盤前摘要 / 板塊輪動 / 異動雷達 / AI 助理）**不是 metric**，
 *  它們是應用程式本身的頁面，登錄表裡沒有它們，也不該有。
 *
 *  lg 以下這條側邊欄不出現，由 BottomTabBar 接手；registry 那一段（`MetricNavGroups`）
 *  兩邊共用，BottomTabBar 的「更多」抽屜畫的是同一份清單。
 *
 *  hover 預取（§6 交付項：`prefetchQuery` on `onMouseEnter`，debounce 120ms；觸控裝置走 `onTouchStart`，
 *  手指一滑動（`onTouchMove`）就取消——捲動清單時掠過的每一列都不該觸發預取）
 *  ----------------------------------------------------------------------
 *  router 的 `defaultPreloadDelay: 120`（main.tsx）預取的是**路由的程式碼**；
 *  這裡預取的是**那一頁的資料**。兩個 120ms 相同是刻意的：一個先一個後，
 *  只會讓其中一個變成沒有用的那一半。
 * ========================================================================== */

const ACTIVE = 'bg-[#48c74e] text-white font-semibold shadow-xs';
const IDLE = 'text-fg-2 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:text-fg-1';
/** 觸控主指標（iPad 橫拿時 lg 也成立）下每一列至少 44px 高。滑鼠下維持原本的密度。 */
const TOUCH_ROW = 'pointer-coarse:min-h-11';

function Item({
  to,
  params,
  label,
  hint: _hint,
  search,
  badge,
  icon,
}: {
  to: string;
  params?: Record<string, string> | undefined;
  label: string;
  hint?: string | undefined;
  search?: Record<string, string> | undefined;
  badge?: string | number | undefined;
  icon?: string | undefined;
}) {
  return (
    <Link
      to={to}
      {...(params === undefined ? {} : { params })}
      {...(search === undefined ? {} : { search })}
      activeOptions={{ exact: false }}
      className={`group flex shrink-0 items-center justify-between gap-2 rounded-pill px-3 py-1.5 leading-tight transition-all duration-150 ${TOUCH_ROW} ${IDLE}`}
      activeProps={{ className: `group flex shrink-0 items-center justify-between gap-2 rounded-pill px-3 py-1.5 leading-tight transition-all duration-150 ${TOUCH_ROW} ${ACTIVE}` }}
    >
      <div className="flex items-center gap-2.5 truncate">
        {icon && <span className="text-13 shrink-0 opacity-80">{icon}</span>}
        <span className="truncate text-13 font-medium">{label}</span>
      </div>
      {badge !== undefined && (
        <span className="font-mono text-10 font-bold px-1.5 py-0.2 rounded-full bg-surface-3 text-fg-2 group-hover:bg-surface-2 group-[.bg-\[\#48c74e\]]:bg-white group-[.bg-\[\#48c74e\]]:text-[#111827]">
          {badge}
        </span>
      )}
    </Link>
  );
}

/** registry 分組（category）的圖示；沒列到的分組用預設圖示。這是分類不是指標，不違反「前端不硬寫 metric 清單」。 */
const GROUP_ICON: Record<string, string> = {
  price: '💹',
  chips: '🏦',
  fundamental: '🧾',
  valuation: '⚖️',
  model: '🤖',
};

/** 一個 registry 項目。**它不知道自己是哪一個 metric，只知道自己的目的地。** */
function MetricItem({
  label,
  destination,
  active,
  onHover,
  onLeave,
  onNavigate,
  chip = false,
}: {
  label: string;
  destination: NavDestination;
  active: boolean;
  onHover: () => void;
  onLeave: () => void;
  onNavigate: (() => void) | undefined;
  /** 手機「更多」抽屜用：膠囊 chip（自動換行排列），而不是側邊欄那種縮排整列。 */
  chip?: boolean;
}) {
  if (destination.kind === 'unavailable') {
    // 灰掉但**留在清單裡**，而且說得出原因——原因是看得見的字，不是只有滑鼠才讀得到的 title。
    // 整列消失的話，使用者會以為那一頁不存在；一條指向 404 的連結則更糟
    // （§10.5.1「永遠不是一張空白卡」同一條原則）。
    return (
      <span
        aria-disabled="true"
        className={`block cursor-not-allowed rounded-control px-2 py-1 leading-tight text-muted ${chip ? 'basis-full' : ''}`}
      >
        <span className="block truncate text-13 whitespace-nowrap">{label}</span>
        <span className="lbl block">{destination.reason}</span>
      </span>
    );
  }

  return (
    <Link
      to={destination.to}
      params={destination.params}
      {...(destination.search === undefined ? {} : { search: destination.search })}
      onMouseEnter={onHover}
      onFocus={onHover}
      onTouchStart={onHover}
      onTouchMove={onLeave}
      onMouseLeave={onLeave}
      onBlur={onLeave}
      onClick={onNavigate}
      className={
        chip
          ? `flex min-h-10 shrink-0 items-center rounded-pill px-3.5 leading-tight transition-all duration-150 ${active ? ACTIVE : 'glass text-fg-1'}`
          : `flex min-w-0 shrink-0 items-center gap-2.5 rounded-pill py-1.5 pr-3 pl-8 leading-tight transition-all duration-150 ${TOUCH_ROW} ${active ? ACTIVE : IDLE}`
      }
    >
      <span className="block truncate text-13 font-medium whitespace-nowrap">{label}</span>
    </Link>
  );
}

/**
 * registry `nav` 的分組清單（桌面側邊欄的 Analytics 段、手機「更多」抽屜共用）。
 * `prefetcher` 由呼叫端持有：抽屜在點下連結的同一刻就關閉並卸載這一段，
 * 預取器若住在這裡，那一筆預取會跟著被取消。
 */
export function MetricNavGroups({
  prefetcher,
  onNavigate,
  layout = 'list',
}: {
  prefetcher: HoverPrefetcher;
  onNavigate?: () => void;
  layout?: 'list' | 'chips';
}) {
  const { market } = useMarket();
  const { param: asOf, today } = useAsOf();
  const symbol = useSymbol(market);
  const queryClient = useQueryClient();

  const registry = useQuery(registryQuery(market, asOf, today));
  // ADR-0002：MODEL_OUTPUT_ACCESS=off（或 /status 還沒回來）時，registry 的模型分組與
  // 模型健檢都不出現。
  const access = useModelOutputAccess();
  const groups = visibleNavGroups(registry.data?.nav ?? [], access);

  /** 目前 URL 上的 metric_key。有的話要標示「你在這裡」。 */
  const currentMetricKey = useCurrentMetricKey();

  const window = useMemo(() => rangeToWindow(DEFAULT_RANGE, asOf ?? today), [asOf, today]);

  return (
    <>
      {/* ── 以下每一列都來自 GET /meta/metrics 的 nav ─────────────────────── */}
      {groups.map((group) => (
        <div key={group.category} className="studio-nav__group block">
          <div
            className={`flex items-center gap-2 pb-1 text-12 font-semibold text-fg-1 ${layout === 'chips' ? 'px-1 pt-4' : 'px-3 pt-3'}`}
          >
            <span aria-hidden="true" className="text-13 opacity-80">
              {GROUP_ICON[group.category] ?? '📌'}
            </span>
            {group.label_zh}
          </div>
          <div className={layout === 'chips' ? 'flex flex-wrap gap-2 px-1 pt-1' : 'contents'}>
          {group.items.map((item) => {
            const destination = navDestination(item.href_template, market, symbol);
            return (
              <MetricItem
                key={item.metric_key}
                label={item.label_zh}
                destination={destination}
                chip={layout === 'chips'}
                active={isActiveDestination(destination, { metricKey: currentMetricKey })}
                onNavigate={onNavigate}
                onHover={() => {
                  if (destination.kind !== 'route') return;
                  // grain='market' 的項目沒有 symbol；那一支端點的預取留給 P3 後段
                  // （它的 staleTime 與個股序列相同，第一次點進去仍然只有一次往返）。
                  if (destination.params['symbol'] === undefined) return;
                  prefetcher.enter(`nav:${item.metric_key}`, () => {
                    void queryClient.prefetchQuery(
                      seriesQuery(market, symbol, item.metric_key, window, asOf, today, undefined),
                    );
                  });
                }}
                onLeave={() => {
                  prefetcher.leave();
                }}
              />
            );
          })}
          </div>
        </div>
      ))}

      {registry.isError && (
        <div className="lbl px-2 py-1 text-warn">
          指標清單讀不到（/meta/metrics）—— 明細頁連結暫時不可用
        </div>
      )}
    </>
  );
}

export function SideNav() {
  const { market } = useMarket();
  const access = useModelOutputAccess();
  const prefetcher = useNavPrefetcher();

  return (
    <nav
      aria-label="主導覽"
      className="sticky top-[var(--topnav-h,3rem)] z-20 hidden h-[calc(100dvh-var(--topnav-h,3rem))] w-[220px] shrink-0 flex-col gap-1.5 self-start overflow-y-auto overscroll-contain border-r border-border-1/60 bg-surface-1/90 p-2.5 pl-[max(0.625rem,var(--safe-left))] backdrop-blur [scrollbar-width:thin] lg:flex"
    >
      <Item to="/$market/dashboard" params={{ market }} label="今日重點" icon="📊" />
      <Item to="/$market/watchlist" params={{ market }} label="自選股" icon="⭐" />
      <Item to="/$market/portfolio" params={{ market }} label="庫存股" icon="💼" />
      <Item to="/$market/assistant" params={{ market }} label="AI 理財助理" icon="🤖" />

      <Item to="/$market/digest" params={{ market }} label="盤前摘要" icon="🗓" />
      <Item to="/$market/market/sector-rotation" params={{ market }} label="板塊輪動" icon="🗂" />
      <Item to="/$market/market/action-radar" params={{ market }} label="異動雷達" icon="⚡" />

      <div className="px-3 pt-3 pb-1 text-[10px] font-bold tracking-wider text-muted uppercase">
        Analytics
      </div>
      <MetricNavGroups prefetcher={prefetcher} />

      {isModelOutputNavVisible(access) && (
        <>
          <div className="px-3 pt-3 pb-1 text-[10px] font-bold tracking-wider text-muted uppercase">
            Model
          </div>
          <Item to="/ai/model-health" search={{ market }} label="模型健檢" icon="📈" />
        </>
      )}
    </nav>
  );
}

/**
 * 目前 URL 的 `$metricKey`（沒有就是 undefined）。
 *
 * 讀 router state 的 `pathname` 最後一段，而不是 `useParams`：SideNav 住在
 * `__root.tsx`，它拿不到子路由的參數，而 `useParams({ strict: false })` 在
 * 不同路由下回傳的形狀不一致。四條 studio 路由的最後一段剛好都是 metric_key。
 *
 * 走 `useRouterState` 而不是 `window.location` 是必要的，不是風格：後者不是
 * React 的資料來源，導覽後不會觸發重新渲染，於是高亮會停在上一頁——
 * 一個「看起來只是偶爾怪怪的」而且只在 client-side 導覽時發生的 bug。
 *
 * 比對用的是**參數值**（`isActiveDestination`），所以比錯了最多是少一個高亮，
 * 不會導錯頁。
 */
function useCurrentMetricKey(): string | undefined {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const segments = pathname.split('/').filter((s) => s !== '');
  return segments.length >= 3 ? segments[segments.length - 1] : undefined;
}
